import React, { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { AlertTriangle, CheckCircle2, X, Building2, User as UserIcon, ArrowRight, RotateCcw } from "lucide-react";

const RESPONSIBILITY_LABELS = {
  newell: "Newell to action",
  mccain: "McCain to action",
  unassigned: "Unassigned",
};
const STATUS_LABELS = {
  open: "Open",
  in_progress: "In progress",
  resolved: "Resolved",
};
const RAG_LABELS = {
  unsafe: "Recommended dismantle / Unsafe",
  attention: "Needs attention",
};
const RAG_COLORS = {
  unsafe: "#dc2626",
  attention: "#f59e0b",
};
const RESP_COLORS = {
  newell: "#0f172a",
  mccain: "#ff5a00",
  unassigned: "#64748b",
};

export default function Reds() {
  const { user } = useAuth();
  const isMcCain = user?.role?.startsWith("mccain") || user?.role === "mccain";
  const canEdit = ["admin", "contracts_manager", "supervisor"].includes(user?.role);

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState(null);
  const [scope, setScope] = useState(isMcCain ? "mine" : "open");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [busyId, setBusyId] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/reds?scope=${scope}`);
      setRows(data.rows || []);
      setCounts(data.counts || null);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [scope]);

  useEffect(() => { refresh(); }, [refresh]);

  const patch = async (id, body, optimisticMsg) => {
    setBusyId(id); setError(""); setSuccess("");
    try {
      await api.patch(`/structures/${id}/action`, body);
      setSuccess(optimisticMsg || "Updated.");
      refresh();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const filtered = useMemo(() => rows, [rows]);

  return (
    <div data-testid="reds-page" style={{ padding: 24 }}>
      <div className="mono" style={{ color: "#dc2626", fontWeight: 600, fontSize: 11, letterSpacing: "0.1em" }}>OPERATIONS · REDS &amp; ACTIONS</div>
      <h1 style={{ fontSize: 26, fontWeight: 900, margin: "4px 0 6px" }}>Reds — action list</h1>
      <p style={{ color: "#64748b", fontSize: 13, marginBottom: 14 }}>
        {isMcCain
          ? "Items requiring action. Items tagged 'McCain to action' are yours to clear (cleaning, line-off, timing). Newell items are tracked for visibility."
          : "Every structure currently red or needing attention, with responsibility and status. A red isn't always Newell's fault — tag McCain-to-action where it's their job to clear."}
      </p>

      {/* COUNT TILES */}
      {counts ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 14 }}>
          <CountTile label="Total" value={counts.total} color="#0f172a" testid="reds-count-total" />
          <CountTile label="Newell open" value={counts.newell_open} color="#0f172a" testid="reds-count-newell" />
          <CountTile label="McCain open" value={counts.mccain_open} color="#ff5a00" testid="reds-count-mccain" />
          <CountTile label="In progress" value={counts.in_progress} color="#f59e0b" testid="reds-count-inprog" />
          <CountTile label="Resolved" value={counts.resolved} color="#059669" testid="reds-count-resolved" />
        </div>
      ) : null}

      {/* SCOPE FILTER */}
      <div className="card" style={{ padding: 10, marginBottom: 12, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <div className="mono" style={{ fontSize: 11, color: "#64748b", marginRight: 6 }}>SHOW:</div>
        {[
          { v: "all", label: "All" },
          { v: "open", label: "Open only" },
          { v: "mine", label: isMcCain ? "McCain to action" : "Newell to action" },
        ].map((s) => (
          <button key={s.v} data-testid={`reds-scope-${s.v}`} onClick={() => setScope(s.v)}
            className={scope === s.v ? "btn-primary" : "btn-ghost"} style={{ fontSize: 12, padding: "5px 10px" }}>{s.label}</button>
        ))}
        <button data-testid="reds-refresh" onClick={refresh} className="btn-ghost" style={{ fontSize: 12, padding: "5px 10px", marginLeft: "auto" }}>
          <RotateCcw size={12} style={{ verticalAlign: -2, marginRight: 4 }} />Refresh
        </button>
      </div>

      {error ? <Banner type="error" testid="reds-error" msg={error} onClose={() => setError("")} /> : null}
      {success ? <Banner type="success" testid="reds-success" msg={success} onClose={() => setSuccess("")} /> : null}

      {filtered.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: "center", color: "#64748b" }}>
          <CheckCircle2 size={28} color="#059669" style={{ margin: "0 auto 8px" }} />
          <div style={{ fontWeight: 700, fontSize: 15 }}>Nothing to action under &quot;{scope}&quot;.</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>All clear — no open reds matching this filter.</div>
        </div>
      ) : null}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {filtered.map((r) => (
          <RedRow key={r.id} r={r} canEdit={canEdit} busy={busyId === r.id}
                  onPatch={(body, msg) => patch(r.id, body, msg)} />
        ))}
      </div>
    </div>
  );
}

function CountTile({ label, value, color, testid }) {
  return (
    <div data-testid={testid} className="card" style={{ padding: 12, borderTop: `3px solid ${color}` }}>
      <div className="mono" style={{ fontSize: 10, color: "#64748b", letterSpacing: "0.1em" }}>{label.toUpperCase()}</div>
      <div style={{ fontSize: 28, fontWeight: 900, color, marginTop: 2 }}>{value}</div>
    </div>
  );
}

function RedRow({ r, canEdit, busy, onPatch }) {
  const ragColor = RAG_COLORS[r.condition_rag] || "#dc2626";
  const respColor = RESP_COLORS[r.responsibility] || "#64748b";
  return (
    <div data-testid={`red-row-${r.id}`} className="card" style={{ padding: 14, borderLeft: `5px solid ${ragColor}`, opacity: r.action_status === "resolved" ? 0.6 : 1 }}>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span className="mono" style={{ fontSize: 10, color: "#64748b" }}>{r.reference}</span>
            <span style={{ fontSize: 9, padding: "2px 6px", background: ragColor, color: "#fff", borderRadius: 2, fontWeight: 700, letterSpacing: "0.05em" }}>{RAG_LABELS[r.condition_rag] || r.condition_rag.toUpperCase()}</span>
            {r.discrepancy ? <span style={{ fontSize: 9, padding: "2px 6px", background: "#fef2f2", color: "#b91c1c", borderRadius: 2, fontWeight: 700, border: "1px solid #fca5a5" }}>DISCREPANCY</span> : null}
          </div>
          <h3 style={{ margin: "4px 0 2px", fontSize: 15, fontWeight: 800 }}>{r.description}</h3>
          <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>Area: <strong>{r.area_name || "—"}</strong></div>
          <div className="card" style={{ padding: 8, background: "#fff7ed", borderLeft: "3px solid #f59e0b", fontSize: 12, color: "#7c2d12" }}>
            <strong>Why it&apos;s red:</strong> {r.reason}
          </div>
          {r.action_note ? (
            <div style={{ marginTop: 6, fontSize: 12, color: "#0f172a" }}>
              <span className="mono" style={{ fontSize: 10, color: "#64748b" }}>ACTION NOTE · </span>{r.action_note}
            </div>
          ) : null}
        </div>

        {/* Right column: tags + controls */}
        <div style={{ minWidth: 260, display: "flex", flexDirection: "column", gap: 8 }}>
          <div className="mono" style={{ fontSize: 10, color: "#64748b", letterSpacing: "0.1em" }}>RESPONSIBILITY</div>
          <div style={{ display: "flex", gap: 4 }}>
            {Object.keys(RESPONSIBILITY_LABELS).map((rk) => (
              <button key={rk} data-testid={`red-${r.id}-resp-${rk}`}
                onClick={() => canEdit && onPatch({ responsibility: rk }, `Responsibility set to ${RESPONSIBILITY_LABELS[rk]}.`)}
                disabled={!canEdit || busy}
                style={{
                  flex: 1, padding: "6px 8px", fontSize: 11, fontWeight: 700,
                  background: r.responsibility === rk ? RESP_COLORS[rk] : "transparent",
                  color: r.responsibility === rk ? "#fff" : RESP_COLORS[rk],
                  border: `1px solid ${RESP_COLORS[rk]}`, borderRadius: 3,
                  cursor: canEdit ? "pointer" : "not-allowed",
                }}>
                {rk === "newell" ? <Building2 size={11} style={{ verticalAlign: -2, marginRight: 4 }} /> : null}
                {rk === "mccain" ? <UserIcon size={11} style={{ verticalAlign: -2, marginRight: 4 }} /> : null}
                {RESPONSIBILITY_LABELS[rk].replace(" to action", "")}
              </button>
            ))}
          </div>
          <div className="mono" style={{ fontSize: 10, color: "#64748b", letterSpacing: "0.1em", marginTop: 4 }}>STATUS</div>
          <div style={{ display: "flex", gap: 4 }}>
            {Object.keys(STATUS_LABELS).map((sk) => (
              <button key={sk} data-testid={`red-${r.id}-status-${sk}`}
                onClick={() => canEdit && onPatch({ action_status: sk }, `Status: ${STATUS_LABELS[sk]}.`)}
                disabled={!canEdit || busy}
                className={r.action_status === sk ? "btn-primary" : "btn-ghost"}
                style={{ flex: 1, fontSize: 11, padding: "5px 8px", fontWeight: 700 }}>
                {STATUS_LABELS[sk]}
              </button>
            ))}
          </div>
          {canEdit ? (
            <button data-testid={`red-${r.id}-add-note`} onClick={() => {
              const note = window.prompt("Action note (what's being done?)", r.action_note || "");
              if (note !== null) onPatch({ action_note: note }, "Note saved.");
            }} disabled={busy} className="btn-ghost" style={{ fontSize: 11 }}>
              {r.action_note ? "Edit note" : "Add note"}
            </button>
          ) : null}
          <a href={`/site-map`} data-testid={`red-${r.id}-map-link`} className="btn-ghost" style={{ fontSize: 11, textAlign: "center" }}>
            View on map <ArrowRight size={11} style={{ verticalAlign: -1 }} />
          </a>
        </div>
      </div>
    </div>
  );
}

function Banner({ type, msg, testid, onClose }) {
  const bg = type === "error" ? "#fef2f2" : "#ecfdf5";
  const fg = type === "error" ? "#b91c1c" : "#065f46";
  const border = type === "error" ? "#dc2626" : "#059669";
  const Icon = type === "error" ? AlertTriangle : CheckCircle2;
  return (
    <div data-testid={testid} className="card" style={{ padding: 10, marginBottom: 10, background: bg, color: fg, borderLeft: `4px solid ${border}`, display: "flex", gap: 8, alignItems: "center" }}>
      <Icon size={14} /><span style={{ flex: 1, fontSize: 12 }}>{msg}</span>
      {onClose ? <button className="btn-ghost" onClick={onClose}><X size={12} /></button> : null}
    </div>
  );
}
